import { useTranslation } from "react-i18next";
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from "react-router";
import { AppShell } from "../components/AppShell.tsx";
import { ErrorState, Loading } from "../components/Status.tsx";
import { CurrentTeamProvider } from "../lib/current-team.tsx";
import { errorText } from "../lib/errors.ts";
import { useMe, useTeams } from "../lib/queries.ts";
import { AccessPage } from "../pages/Access.tsx";
import { CalloutAdminPage } from "../pages/CalloutAdmin.tsx";
import { MapMeshAdminPage } from "../pages/MapMeshAdmin.tsx";
import { ChangelogPage } from "../pages/Changelog.tsx";
import { ConfigBuilderPage } from "../pages/ConfigBuilder.tsx";
import { ConfigDetailPage } from "../pages/ConfigDetail.tsx";
import { ConfigsPage, NewConfigPage, PracticeConfigPage } from "../pages/Configs.tsx";
import { DemoImportPage } from "../pages/DemoImport.tsx";
import { DemosPage } from "../pages/Demos.tsx";
import { MatchesPage, MatchPage } from "../pages/Matches.tsx";
import { DemoViewerPage } from "../pages/DemoViewer.tsx";
import { ComboDetailPage } from "../pages/ComboDetail.tsx";
import { EditComboPage, NewComboPage } from "../pages/ComboForm.tsx";
import { EditExecutionPage, NewExecutionPage } from "../pages/ExecutionBuilder.tsx";
import { ExecutionsPage } from "../pages/Executions.tsx";
import { ImportStuffsPage } from "../pages/ImportStuffs.tsx";
import { JoinPage } from "../pages/Join.tsx";
import { LeaderboardPage } from "../pages/Leaderboard.tsx";
import { EditPlantSpotPage, NewPlantSpotPage } from "../pages/PlantSpotForm.tsx";
import { PostPlantPage } from "../pages/PostPlant.tsx";
import { StratsPage } from "../pages/Strats.tsx";
import { LearnPage } from "../pages/Learn.tsx";
import { LivePage } from "../pages/Live.tsx";
import { LiveSetupPage } from "../pages/LiveSetup.tsx";
import { MapPickerPage } from "../pages/MapPicker.tsx";
import { MapViewPage } from "../pages/MapView.tsx";
import { NotFoundPage } from "../pages/NotFound.tsx";
import { SetupPage } from "../pages/Setup.tsx";
import { SignInPage } from "../pages/SignIn.tsx";
import { StuffDetailPage } from "../pages/StuffDetail.tsx";
import { EditStuffPage, NewStuffPage } from "../pages/StuffForm.tsx";
import { StuffListPage } from "../pages/StuffList.tsx";
import { TeamPage } from "../pages/Team.tsx";
import { DiscordLinkPage, DiscordTeamPage } from "../pages/DiscordLink.tsx";

/** Loads who is signed in and their teams once, for every page. */
function Session({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const me = useMe();
  const signedIn = Boolean(me.data?.user);
  const teams = useTeams(signedIn);
  if (me.isPending || (signedIn && teams.isPending)) return <Loading />;
  if (me.error) {
    // In development the usual cause is the API not running, or another program on its port.
    const message = import.meta.env.DEV ? t("common.apiDown") : errorText(t, me.error);
    return <ErrorState message={message} onRetry={() => void me.refetch()} />;
  }
  return <CurrentTeamProvider teams={teams.data ?? []}>{children}</CurrentTeamProvider>;
}

/** Pages behind sign-in send visitors to Steam sign-in, then back. */
function RequireUser() {
  const me = useMe().data?.user;
  const location = useLocation();
  if (!me) {
    const next = location.pathname + location.search;
    return <Navigate to={`/sign-in?next=${encodeURIComponent(next)}`} replace />;
  }
  return <Outlet />;
}

/** New players without a team go to setup; everyone else to the library. */
function Home() {
  const teams = useTeams().data ?? [];
  return <Navigate to={teams.length ? "/library" : "/setup"} replace />;
}

export function AppRoutes() {
  return (
    <Session>
      <Routes>
        <Route path="/sign-in" element={<SignInPage />} />
        <Route path="/join/:code" element={<JoinPage />} />
        <Route element={<RequireUser />}>
          <Route index element={<Home />} />
          <Route path="/setup" element={<SetupPage />} />
          <Route element={<AppShell />}>
            <Route path="/library" element={<MapPickerPage />} />
            <Route path="/library/:map" element={<MapViewPage />} />
            <Route path="/library/:map/list" element={<StuffListPage />} />
            <Route path="/library/:map/learn" element={<LearnPage />} />
            <Route path="/library/:map/combos/new" element={<NewComboPage />} />
            <Route path="/combos/:id" element={<ComboDetailPage />} />
            <Route path="/combos/:id/edit" element={<EditComboPage />} />
            <Route path="/library/:map/executions" element={<ExecutionsPage />} />
            <Route path="/library/:map/executions/new" element={<NewExecutionPage />} />
            <Route path="/executions/:id/edit" element={<EditExecutionPage />} />
            <Route path="/library/:map/post-plant" element={<PostPlantPage />} />
            <Route path="/library/:map/strats" element={<StratsPage />} />
            <Route path="/library/:map/demos" element={<DemosPage />} />
            <Route path="/demos/import" element={<DemoImportPage />} />
            <Route path="/demos/:id" element={<DemoViewerPage />} />
            <Route path="/matches" element={<MatchesPage />} />
            <Route path="/matches/:id" element={<MatchPage />} />
            <Route path="/pro" element={<Navigate to="/matches?label=pro" replace />} />
            <Route path="/library/:map/post-plant/new" element={<NewPlantSpotPage />} />
            <Route path="/plant-spots/:id/edit" element={<EditPlantSpotPage />} />
            <Route path="/library/:map/new" element={<NewStuffPage />} />
            <Route path="/library/:map/import" element={<ImportStuffsPage />} />
            <Route path="/stuffs/:id" element={<StuffDetailPage />} />
            <Route path="/stuffs/:id/edit" element={<EditStuffPage />} />
            <Route path="/team" element={<TeamPage />} />
            <Route path="/discord/link" element={<DiscordLinkPage />} />
            <Route path="/discord/team" element={<DiscordTeamPage />} />
            <Route path="/access" element={<AccessPage />} />
            <Route path="/admin/callouts" element={<CalloutAdminPage />} />
            <Route path="/admin/maps" element={<MapMeshAdminPage />} />
            <Route path="/changelog" element={<ChangelogPage />} />
            <Route path="/live" element={<LivePage />} />
            <Route path="/live/setup" element={<LiveSetupPage />} />
            <Route path="/configs" element={<ConfigsPage />} />
            <Route path="/configs/new" element={<NewConfigPage />} />
            <Route path="/configs/build" element={<ConfigBuilderPage />} />
            <Route path="/configs/practice" element={<PracticeConfigPage />} />
            <Route path="/configs/:id" element={<ConfigDetailPage />} />
            <Route path="/leaderboard" element={<LeaderboardPage />} />
          </Route>
        </Route>
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Session>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  );
}
